import { useEffect, useRef } from "react";
import { useSite } from "@/hooks/use-site";

export function SocialProof() {
  const { messages } = useSite();
  const trackRef = useRef<HTMLDivElement>(null);
  const logos = [
    "Metroport",
    "Volya",
    "OK.TV",
    "Kyivstar",
    "Alma.tv",
    "MEGOGO",
    "ConectaBalear",
    "Ukrtelecom",
    "Sweet.tv",
    "Datagroup",
    "Beeline Kazakhstan",
    "Lanet Network",
    "TENET",
    "Cosmonova",
    "NashNet",
    "SkyTel",
    "K@TV1",
    "Sarkor Telecom",
    "DVL Group",
    "Vega Telecom",
    "Triolan",
    "Maxnet",
    "Tel-Kab Poland",
    "Prosto.TV",
    "Skynet Teletcom",
    "Interlink",
    "Columbus",
  ];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const touchMobile = window.matchMedia(
      "(max-width: 767px) and (hover: none) and (pointer: coarse)",
    );
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let copyWidth = 0;
    let dragging = false;
    let resumeAt = 0;
    let previousTime = performance.now();
    let animationFrame = 0;

    const normalizePosition = () => {
      if (!copyWidth) return;
      if (track.scrollLeft > copyWidth * 1.5) track.scrollLeft -= copyWidth;
      else if (track.scrollLeft < copyWidth * 0.5) track.scrollLeft += copyWidth;
    };

    const measure = () => {
      const firstCopy = track.querySelector<HTMLElement>("[data-social-proof-copy]");
      copyWidth = firstCopy?.getBoundingClientRect().width ?? track.scrollWidth / 3;
      if (touchMobile.matches && track.scrollLeft === 0) {
        track.scrollLeft = copyWidth;
      }
    };

    const release = () => {
      dragging = false;
      resumeAt = performance.now() + 700;
    };

    const handleTouchStart = () => {
      dragging = true;
      resumeAt = Number.POSITIVE_INFINITY;
    };

    const handleScroll = () => {
      if (!touchMobile.matches) return;
      normalizePosition();

      // Native momentum continues after touchend. Each momentum scroll extends
      // the pause slightly, then the automatic movement picks up naturally.
      const now = performance.now();
      if (!dragging && now < resumeAt) resumeAt = now + 180;
    };

    const handleMediaChange = () => {
      previousTime = performance.now();
      if (touchMobile.matches) measure();
      else track.scrollLeft = 0;
    };

    const animate = (time: number) => {
      const elapsed = Math.min(time - previousTime, 64);
      previousTime = time;

      if (touchMobile.matches && !reducedMotion.matches && !dragging && time >= resumeAt) {
        normalizePosition();
        track.scrollLeft += (copyWidth / 50_000) * elapsed;
      }

      animationFrame = window.requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);
    const firstCopy = track.querySelector<HTMLElement>("[data-social-proof-copy]");
    if (firstCopy) resizeObserver.observe(firstCopy);
    track.addEventListener("touchstart", handleTouchStart, { passive: true });
    track.addEventListener("touchend", release, { passive: true });
    track.addEventListener("touchcancel", release, { passive: true });
    track.addEventListener("scroll", handleScroll, { passive: true });
    touchMobile.addEventListener("change", handleMediaChange);
    measure();
    animationFrame = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      track.removeEventListener("touchstart", handleTouchStart);
      track.removeEventListener("touchend", release);
      track.removeEventListener("touchcancel", release);
      track.removeEventListener("scroll", handleScroll);
      touchMobile.removeEventListener("change", handleMediaChange);
    };
  }, []);

  return (
    <section className="relative overflow-x-clip border-y border-border/60 section-tint">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <p className="text-center text-base font-medium tracking-tight text-foreground sm:text-lg">
          {messages.trustedBy}
        </p>
        <div
          ref={trackRef}
          className="social-proof-track relative mt-8 max-w-full overflow-x-clip [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
        >
          <div className="social-proof-marquee flex w-max">
            {[0, 1, 2].map((copyIndex) => (
              <div
                key={copyIndex}
                aria-hidden={copyIndex !== 1 || undefined}
                data-social-proof-copy
                className="social-proof-marquee-copy flex shrink-0 gap-14 pr-14"
              >
                {logos.map((name) => (
                  <div
                    key={name}
                    className="flex h-8 shrink-0 items-center justify-center text-base font-semibold tracking-tight text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {name}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
